// LegalPage: shared layout for all legal/info pages.
//
// Shows which version of the policy is being read and when it took effect.
// A retired version renders with a clear banner and a link to the current
// one, so nobody mistakes old wording for the policy in force.
import { Link } from "react-router-dom";
import { LANGUAGE_CLAUSE } from "@/content/legal";
import { POLICY_BY_SLUG, currentVersion, formatEffective, previousVersions } from "@/content/policyVersions";
import { SectionBody } from "@/components/MarketingPage";
import { useT } from "@/i18n/I18nProvider";
import CachePublicShell from "@/components/CachePublicShell";
import OperatorNotice from "@/components/OperatorNotice";

export default function LegalPage({ content, slug = "legal", archivedVersion = null }) {
  const { lang } = useT();
  const resolved = content[lang] || content.en;
  const { title, intro, sections } = resolved;
  const tl = lang === "tl";
  const policy = POLICY_BY_SLUG[slug];
  const current = currentVersion(slug);
  const shown = archivedVersion || current;
  const older = archivedVersion ? [] : previousVersions(slug);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
         data-testid={`legal-page-${slug}`}>
      <CachePublicShell />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        ConfirmPH · Legal
      </p>

      {archivedVersion && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
             role="note" data-testid="legal-archived-banner">
          {tl
            ? "Ito ay isang lumang bersyon na hindi na ipinapatupad. Nandito ito para makita kung ano ang eksaktong nakasulat noon."
            : "This is a retired version that is no longer in force. It is kept so anyone can see exactly what it said."}
          {policy && (
            <>
              {" "}
              <Link to={policy.path} className="underline font-medium" data-testid="legal-archived-current-link">
                {tl ? "Basahin ang kasalukuyang bersyon" : "Read the current version"}
              </Link>
            </>
          )}
        </div>
      )}

      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2"
          data-testid={`legal-title-${slug}`}>
        {title}
      </h1>
      {shown && (
        <p className="text-xs text-slate-500 mt-1" data-testid={`legal-version-${slug}`}>
          {tl ? "Bersyon" : "Version"} {shown.version} ·{" "}
          {tl ? "Epektibo mula" : "Effective"} {formatEffective(shown.effective, lang)}
          {archivedVersion && current && (
            <> · {tl ? "Pinalitan noong" : "Replaced on"} {formatEffective(current.effective, lang)}</>
          )}
        </p>
      )}
      {older.length > 0 && (
        <p className="text-xs text-slate-500 mt-1" data-testid={`legal-previous-${slug}`}>
          {tl ? "Mga naunang bersyon:" : "Previous versions:"}{" "}
          {older.map((v, i) => (
            <span key={v.version}>
              {i > 0 && ", "}
              <Link to={`/legal/archive/${slug}/${v.archive}`} className="underline">
                {formatEffective(v.effective, lang)}
              </Link>
            </span>
          ))}
        </p>
      )}

      {intro && (
        <p className="mt-6 text-slate-700 leading-relaxed">{intro}</p>
      )}

      <div className="mt-8 space-y-8">
        {sections.map((s, i) => (
          <section key={i} data-testid={`legal-section-${slug}-${i}`}>
            <h2 className="font-display text-xl font-semibold">{s.h}</h2>
            <SectionBody paragraphs={s.body} />
          </section>
        ))}
      </div>

      <p className="mt-8 text-xs text-slate-500 leading-relaxed">
        {LANGUAGE_CLAUSE[lang] || LANGUAGE_CLAUSE.en}
      </p>

      {!archivedVersion && <OperatorNotice />}

      <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-2">
        <Link to="/legal" className="underline" data-testid="legal-policy-centre">
          {tl ? "Lahat ng policy" : "All policies"}
        </Link>
        <Link to="/" className="underline" data-testid="legal-back-home">
          {tl ? "← Bumalik sa home" : "← Back to home"}
        </Link>
      </div>
    </div>
  );
}
